import React, { useEffect, useMemo, useState } from "react";
import { api, newIdempotencyKey } from "../../lib/api.js";
import { followLink } from "../../lib/router.js";
import { StatStrip, FilterRow, PageHead, StatePill, Notice, fmtWhen, INVITATION_STATES } from "./ui.jsx";
import BatchDrawer from "./BatchDrawer.jsx";
import { useConfirm } from "../ConfirmDialog.jsx";

const STATES = Object.keys(INVITATION_STATES);
// Flow B's state filters; "Sent but not registered" covers sent and opened.
const STATE_FILTERS = [
  { value: "created", label: "Not yet sent", states: ["created"] },
  { value: "waiting", label: "Sent but not registered", states: ["sent", "opened"] },
  { value: "registered", label: "Registered", states: ["registered"] },
  { value: "bounced", label: "Bounced", states: ["bounced"] },
  { value: "revoked", label: "Withdrawn", states: ["revoked"] },
];
const PAGE = 25;
const MAX_SEND = 1000;   // the send call takes at most this many at once

export default function InvitationsPage({ data, reload, isAdmin }) {
  const [who, setWho] = useState("all");
  const [state, setState] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [picked, setPicked] = useState(() => new Set());
  const [wave, setWave] = useState(100);
  const [drawer, setDrawer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, ask] = useConfirm();

  const c = data.counts ?? {};
  // shortcut: filters, pages and "select all" work in the browser over the list the API returns (up to 2,000);
  // move them to the server if an event outgrows that (the food show might).
  const all = data.invitations ?? [];
  const noBadges = data.event?.badgeMode === "none";
  // Withdrawn, then invited again: the newest live invitation per address, so the old row can say it was replaced.
  // shortcut: matches by email, which is the identity for reps and guests; customers (by master chain) need the API's rule.
  const liveSince = useMemo(() => {
    const m = new Map();
    for (const i of all) if (i.state !== "revoked") { const k = i.email.toLowerCase(); if (!(m.get(k) > i.createdAt)) m.set(k, i.createdAt); }
    return m;
  }, [all]);
  const replacedOn = (i) => i.state === "revoked" && liveSince.get(i.email.toLowerCase()) > i.createdAt ? liveSince.get(i.email.toLowerCase()) : null;
  const inState = (i, f) => f === "all" || STATE_FILTERS.find(s => s.value === f).states.includes(i.state);
  const rows = useMemo(() => all.filter(i =>
    (who === "all" || (who === "guest" ? i.kind === "guest" : i.kind !== "guest")) &&
    inState(i, state) &&
    (!q || `${i.name} ${i.email} ${i.codes ?? ""}`.toLowerCase().includes(q.toLowerCase()))), [all, who, state, q]);
  useEffect(() => { setPage(0); setPicked(new Set()); }, [who, state, q]);   // ticks only ever cover what's on screen
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const at = Math.min(page, pages - 1);
  const shown = rows.slice(at * PAGE, at * PAGE + PAGE);

  // Only unsent invitations can be picked; anything sent meanwhile drops out of the selection.
  const chosen = all.filter(i => i.state === "created" && picked.has(i.id));
  const matchUnsent = rows.filter(i => i.state === "created");
  const toggle = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  async function send(body, done) {
    setBusy(true); setMsg(null); setError(null);
    try {
      const r = await api("/backoffice/invitations/send", { method: "POST", body, idempotencyKey: newIdempotencyKey() });
      setMsg(`${r.queued} queued to send${r.remaining ? `; ${r.remaining} still unsent` : "; none left unsent"}. Watch the states settle before the next wave.`);
      done?.();
      await reload();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function revoke(inv) {
    if (!(await ask({ title: `Withdraw the invitation for ${inv.name}?`, body: "Their link will stop working.", confirmLabel: "Withdraw", cancelLabel: "Keep" }))) return;
    try { await api(`/backoffice/invitations/${inv.id}/revoke`, { method: "POST", body: {} }); await reload(); }
    catch (err) { setError(err.message); }
  }

  return (
    <>
      <PageHead title="Invitations" sub="Each invitation is a key. It only works for the account it was sent to, and only once. Pick who to send to, or send in waves, and watch the states settle."
        aside={isAdmin && <button className="bo-btn primary" onClick={() => setDrawer(true)}>New batch</button>} />
      <StatStrip items={STATES.map(s => ({ k: INVITATION_STATES[s], v: c[s] ?? 0, color: s === "registered" ? "#2f6b3a" : s === "bounced" || s === "revoked" ? "#8a2a1d" : undefined }))} />
      <FilterRow label="Who" value={who} onChange={setWho} options={[
        { value: "all", label: "Everyone", count: all.length, tone: "dark" },
        { value: "lipari", label: "Lipari employees", count: all.filter(i => i.kind !== "guest").length },
        { value: "guest", label: "Guests", count: all.filter(i => i.kind === "guest").length },
      ]} />
      <div className="bo-filter-row">
        <FilterRow label="State" value={state} onChange={setState} options={[{ value: "all", label: "Any state", count: all.length, tone: "roma" },
          ...STATE_FILTERS.map(f => ({ value: f.value, label: f.label, count: f.states.reduce((n, s) => n + (c[s] ?? 0), 0) }))]} />
        <input className="bo-search" placeholder="Search name, email or code" value={q} onChange={e => setQ(e.target.value)} />
      </div>

      {isAdmin && (
        <div className="bo-bar">
          <b>{chosen.length ? `${chosen.length} selected` : "Nobody selected"}</b>
          <button className="bo-btn" disabled={!matchUnsent.length} onClick={() => setPicked(new Set(matchUnsent.map(i => i.id)))}>Select all {matchUnsent.length} that match</button>
          {chosen.length > 0 && <button className="bo-link" onClick={() => setPicked(new Set())}>Clear</button>}
          <button className="bo-btn primary" disabled={busy || !chosen.length || chosen.length > MAX_SEND}
            onClick={() => send({ ids: chosen.map(i => i.id), limit: chosen.length }, () => setPicked(new Set()))}>Send selected ({chosen.length})</button>
          {chosen.length > MAX_SEND && <span className="muted small">Send at most {MAX_SEND.toLocaleString()} at a time.</span>}
          <div className="spacer" />
          <span className="muted">or send the next</span>
          <input className="bo-num" type="number" min={1} max={MAX_SEND} value={wave} onChange={e => setWave(e.target.value)} aria-label="Wave size" />
          <button className="bo-btn" disabled={busy || !(c.created > 0)} onClick={() => send({ limit: Number(wave) })}>Send next {Math.min(Number(wave) || 0, c.created ?? 0)}</button>
        </div>
      )}
      {msg && <Notice tone="ok">{msg}</Notice>}
      {error && <Notice tone="bad">{error}</Notice>}

      <div className="bo-table inv">
        <div className="bo-th"><div /><div>Name</div><div>Contact on file</div><div>Seats</div><div>State</div><div>Sent</div><div>Opened</div><div>Code</div><div /></div>
        {shown.map(i => (
          <div className="bo-tr" key={i.id}>
            <div>{isAdmin && i.state === "created" && <input type="checkbox" aria-label={`Select ${i.name} (${i.email})`} checked={picked.has(i.id)} onChange={() => toggle(i.id)} />}</div>
            <div className="name"><a className="record-link" href={`/admin/record/${i.id}`} onClick={followLink(`/admin/record/${i.id}`)}><b>{i.name}</b></a>{i.kind === "guest" && <span className="tag">guest</span>}</div>
            <div><div className="mono">{i.email}</div><div className="muted small">{i.bounceReason ? `Bounced: ${i.bounceReason}` : i.state === "bounced" ? "Bounced" : replacedOn(i) ? `Replaced by a newer invitation (${fmtWhen(replacedOn(i))})` : "Looks fine"}</div></div>
            <div className="mono">{noBadges ? i.travellersAllowed : `${i.travellersAllowed} / ${i.badgesAllowed}`}</div>
            <div><StatePill state={i.state} /></div>
            <div className="muted">{fmtWhen(i.sentAt)}</div>
            <div className="muted">{fmtWhen(i.openedAt)}</div>
            {/* Each code stays whole: "AM-110310" never breaks at its hyphen. */}
            <div className="mono code">{(i.codes ?? "").split(", ").filter(Boolean).map((c, n) => <React.Fragment key={c}>{n > 0 && ", "}<span className="nowrap">{c}</span></React.Fragment>)}</div>
            <div className="actions">{isAdmin && ["created", "sent", "opened", "bounced"].includes(i.state) && <button className="bo-link" onClick={() => revoke(i)}>Withdraw</button>}</div>
          </div>
        ))}
        {!rows.length && <div className="bo-empty">{all.length ? "Nothing matches those filters." : "No invitations yet. Create a batch to start."}</div>}
      </div>
      {rows.length > 0 && (
        <div className="bo-pager">
          <span>{at * PAGE + 1}–{at * PAGE + shown.length} of {rows.length}</span>
          <button className="bo-link" disabled={at === 0} onClick={() => setPage(at - 1)}>Previous</button>
          <button className="bo-link" disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>Next</button>
        </div>
      )}

      {dialog}
      {drawer && <BatchDrawer noBadges={noBadges} onClose={() => setDrawer(false)} onDone={async (m) => { setDrawer(false); setMsg(m); await reload(); }} />}
    </>
  );
}
